import { useEffect, useMemo, useState } from 'react';
import { LayerGroup, LayersControl, TileLayer, WMSTileLayer, useMap } from 'react-leaflet';
import type { LeafletEventHandlerFn } from 'leaflet';
import { useTranslation } from 'react-i18next';
import { useIcasToggles } from '@/features/icas/useIcasToggles';
import { IcasGeoJsonLayer } from '@/features/icas/IcasGeoJsonLayer';
import { IcasPmtilesLayer } from '@/features/icas/IcasPmtilesLayer';
import { STANDS_CONFIG, TRUPURI_CONFIG } from '@/features/icas/pmtilesConfigs';
import type { IcasLayerKey } from '@/features/icas/types';
import { SubParcelsMapLayer } from '@/features/subparcels/SubParcelsMapLayer';
import { useSubParcelsLayerEnabled } from '@/features/subparcels/useSubParcelsLayer';
import { GoogleAttribution } from './GoogleAttribution';
import { ImageryDateReadout } from './ImageryDateReadout';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAX_NATIVE_ZOOM, googleTileUrl } from './googleTiles';
import {
  ESRI_IMAGERY_URL,
  NATIONAL_ORTHO_WMS_URL,
  orthoWmsLayers,
  orthoYearRange,
} from './imagery';
import { useGoogleTilesSession } from './useGoogleTilesSession';
import { useNationalOrthoLayers } from './useNationalOrthoLayers';

const STORAGE_KEY = 'forest.mapTileLayer';

export type MapLayerKey = 'osm' | 'satellite' | 'google' | 'ortho' | 'topo' | 'positron';

const LAYER_KEYS: MapLayerKey[] = ['osm', 'satellite', 'google', 'ortho', 'topo', 'positron'];

function readStoredLayer(): MapLayerKey | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    // The Sentinel-2 layer was removed; keep its users on photo imagery.
    if (v === 'sentinel') return 'satellite';
    // Without a key the Google layer is not offered at all.
    if (v === 'google' && !GOOGLE_MAPS_API_KEY) return 'satellite';
    if (v && (LAYER_KEYS as string[]).includes(v)) return v as MapLayerKey;
  } catch {
    // ignore
  }
  return null;
}

function writeStoredLayer(key: MapLayerKey): void {
  try {
    localStorage.setItem(STORAGE_KEY, key);
  } catch {
    // ignore
  }
}

type Props = {
  defaultLayer?: MapLayerKey;
};

const ESRI_ATTRIBUTION =
  'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community';

const TOPO_ATTRIBUTION =
  'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, SRTM | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)';

const POSITRON_ATTRIBUTION =
  '&copy; OpenStreetMap contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

const OSM_ATTRIBUTION = '&copy; OpenStreetMap contributors';

const GOOGLE_ATTRIBUTION = '&copy; Google';

// Drawn until the Google session arrives, so the layer never requests tiles without one.
const BLANK_TILE = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

const orthoAttribution = (years: string) =>
  `Ortofoto ${years} &copy; <a href="https://geodata.gov.md">geodata.gov.md</a>`;

/**
 * Shared base-layer switcher (RQ-06).
 * Persists the active selection per-origin in localStorage.
 */
export function MapLayerControl({ defaultLayer = 'osm' }: Props) {
  const { t } = useTranslation(['common', 'database']);
  const initial: MapLayerKey = useMemo(
    () => readStoredLayer() ?? defaultLayer,
    [defaultLayer],
  );
  const [active, setActive] = useState<MapLayerKey>(initial);
  const map = useMap();
  const { toggles, setToggle } = useIcasToggles();
  const { enabled: subParcelsEnabled, setEnabled: setSubParcelsEnabled } =
    useSubParcelsLayerEnabled();
  const orthoLayers = useNationalOrthoLayers();
  const googleSession = useGoogleTilesSession(active === 'google');
  // react-leaflet calls setParams (and redraws every tile) whenever this
  // object's identity changes, so it must only change with the survey list.
  const orthoParams = useMemo(
    () => ({
      layers: orthoWmsLayers(orthoLayers),
      format: 'image/vnd.jpeg-png',
      transparent: true,
    }),
    [orthoLayers],
  );

  useEffect(() => {
    if (!map) return;
    const onBaseChange = (e: { name: string }) => {
      const name = e.name?.toLowerCase() ?? '';
      // Map display names (which are translated) back to keys via the order:
      // we tag each base layer with a known suffix in its name to find it.
      let key: MapLayerKey | null = null;
      if (name.includes('google')) key = 'google';
      else if (name.includes('ortofoto')) key = 'ortho';
      else if (name.includes('osm') || name.includes('strada') || name.includes('street'))
        key = 'osm';
      else if (name.includes('satel')) key = 'satellite';
      else if (name.includes('topo')) key = 'topo';
      else if (name.includes('sobru') || name.includes('positron') || name.includes('light'))
        key = 'positron';
      if (key) {
        setActive(key);
        writeStoredLayer(key);
      }
    };
    map.on('baselayerchange', onBaseChange as unknown as LeafletEventHandlerFn);
    return () => {
      map.off('baselayerchange', onBaseChange as unknown as LeafletEventHandlerFn);
    };
  }, [map]);

  // Mirror Leaflet's overlay-checkbox state into the shared ICAS store so
  // toggling inside the LayersControl widget persists to localStorage and
  // propagates to every map surface. The display names carry a stable
  // `[icas:<key>]` suffix that we strip for the user-visible label.
  useEffect(() => {
    if (!map) return;
    const onOverlay = (e: { name: string; type: string }) => {
      // Subpolygons use the same suffix trick as ICAS so their checkbox
      // state persists and follows the user to every other map surface.
      if (/\[subparcels\]/.test(e.name ?? '')) {
        setSubParcelsEnabled(e.type === 'overlayadd');
        return;
      }
      const m = /\[icas:(entitati|trupuri|stands)\]/.exec(e.name ?? '');
      if (!m) return;
      const key = m[1] as IcasLayerKey;
      setToggle(key, e.type === 'overlayadd');
    };
    map.on('overlayadd', onOverlay as unknown as LeafletEventHandlerFn);
    map.on('overlayremove', onOverlay as unknown as LeafletEventHandlerFn);
    return () => {
      map.off('overlayadd', onOverlay as unknown as LeafletEventHandlerFn);
      map.off('overlayremove', onOverlay as unknown as LeafletEventHandlerFn);
    };
  }, [map, setToggle, setSubParcelsEnabled]);

  return (
    <>
      {(active === 'satellite' || active === 'ortho') && (
        <ImageryDateReadout layer={active} />
      )}
      {active === 'google' && <GoogleAttribution session={googleSession} />}
      <LayersControl position="topright">
        <LayersControl.BaseLayer
          name={`OSM · ${t('common:map.layer.osm', { defaultValue: 'Strada' })}`}
          checked={active === 'osm'}
        >
          <TileLayer
            attribution={OSM_ATTRIBUTION}
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer
          name={`Satelit · ${t('common:map.layer.satellite', { defaultValue: 'Satelit' })}`}
          checked={active === 'satellite'}
        >
          <TileLayer attribution={ESRI_ATTRIBUTION} url={ESRI_IMAGERY_URL} />
        </LayersControl.BaseLayer>
        {GOOGLE_MAPS_API_KEY && (
          <LayersControl.BaseLayer
            name={t('common:map.layer.google', { defaultValue: 'Google Satelit' })}
            checked={active === 'google'}
          >
            <TileLayer
              attribution={GOOGLE_ATTRIBUTION}
              url={googleSession ? googleTileUrl(googleSession, GOOGLE_MAPS_API_KEY) : BLANK_TILE}
              maxNativeZoom={GOOGLE_MAX_NATIVE_ZOOM}
              maxZoom={21}
            />
          </LayersControl.BaseLayer>
        )}
        {/*
          Every recent survey AGCC publishes is stacked here, newest on top,
          with Esri underneath filling whatever they do not cover.
        */}
        <LayersControl.BaseLayer
          name={`Ortofoto · ${t('common:map.layer.ortho', { defaultValue: 'Ortofoto național' })}`}
          checked={active === 'ortho'}
        >
          <LayerGroup>
            <TileLayer attribution={ESRI_ATTRIBUTION} url={ESRI_IMAGERY_URL} />
            {/* Keyed on the years: react-leaflet never updates attribution in place. */}
            <WMSTileLayer
              key={orthoYearRange(orthoLayers)}
              attribution={orthoAttribution(orthoYearRange(orthoLayers))}
              url={NATIONAL_ORTHO_WMS_URL}
              params={orthoParams}
            />
          </LayerGroup>
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer
          name={`Topo · ${t('common:map.layer.topo', { defaultValue: 'Topografic' })}`}
          checked={active === 'topo'}
        >
          <TileLayer
            attribution={TOPO_ATTRIBUTION}
            url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
            maxZoom={17}
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer
          name={`Sobru · ${t('common:map.layer.light', { defaultValue: 'Sobru' })}`}
          checked={active === 'positron'}
        >
          <TileLayer
            attribution={POSITRON_ATTRIBUTION}
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
          />
        </LayersControl.BaseLayer>

        {/*
          ICAS reference overlays. `[icas:<key>]` is a stable suffix our
          `overlayadd`/`overlayremove` handler greps for to persist the
          toggle into the shared store. `checked` reads from the store so
          a remount picks up the user's last choice.
        */}
        {/*
          Subpolygons (receptions included). Mounted here rather than per
          surface so every map with <MapLayerControl /> gets the layer and its
          checkbox for free. Never also render <SubParcelsMapLayer />
          standalone next to this control: it would draw twice.
        */}
        <LayersControl.Overlay
          name={`${t('database:subparcels.layerName', { defaultValue: 'Subpoligoane' })}  [subparcels]`}
          checked={subParcelsEnabled}
        >
          <SubParcelsMapLayer />
        </LayersControl.Overlay>
        <LayersControl.Overlay
          name={`${t('common:icas.layer.entitati')}  [icas:entitati]`}
          checked={toggles.entitati}
        >
          <IcasGeoJsonLayer layer="entitati" enabled />
        </LayersControl.Overlay>
        <LayersControl.Overlay
          name={`${t('common:icas.layer.trupuri')}  [icas:trupuri]`}
          checked={toggles.trupuri}
        >
          <IcasPmtilesLayer config={TRUPURI_CONFIG} enabled />
        </LayersControl.Overlay>
        <LayersControl.Overlay
          name={`${t('common:icas.layer.stands')}  [icas:stands]`}
          checked={toggles.stands}
        >
          <IcasPmtilesLayer config={STANDS_CONFIG} enabled />
        </LayersControl.Overlay>
      </LayersControl>
    </>
  );
}
